import React, { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  CalendarIcon, Minus, Plus, Loader2, CreditCard, User, ArrowRight, ArrowLeft, Tag, Check, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Price, useCurrency } from "@/context/CurrencyContext";

const STEPS = ["Dates & travelers", "Contact", "Passport details", "Review", "Confirmed"];

export default function BookingWidget({ tour }) {
  const { user } = useAuth();
  const { format: fmtPrice, currency } = useCurrency();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [contact, setContact] = useState({
    email: user?.email || "",
    phone: user?.phone || "",
    notes: "",
  });
  const [travelers, setTravelers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [promo, setPromo] = useState(null); // { code, percent_off, description }
  const [bookingRef, setBookingRef] = useState("");

  const unit = tour.price_usd || Math.round((tour.price_from || 0) / 83);
  const subtotal = useMemo(
    () => Math.round(unit * adults + unit * 0.6 * children),
    [unit, adults, children]
  );
  const discount = useMemo(
    () => (promo ? Math.round((subtotal * promo.percent_off) / 100) : 0),
    [promo, subtotal]
  );
  const total = useMemo(() => Math.max(1, subtotal - discount), [subtotal, discount]);

  // Initialize traveler slots
  React.useEffect(() => {
    const blanks = Array.from({ length: adults + children }, (_, i) => ({
      full_name: "",
      passport_number: "",
      passport_expiry: "",
      nationality: "",
      type: i < adults ? "adult" : "child",
    }));
    setTravelers((prev) => {
      const merged = blanks.map((b, i) => ({ ...b, ...(prev[i] || {}), type: b.type }));
      return merged;
    });
  }, [adults, children]);

  const updateTraveler = (idx, key, val) =>
    setTravelers((prev) => prev.map((t, i) => (i === idx ? { ...t, [key]: val } : t)));

  const canProceed = () => {
    if (step === 0) return !!date && adults >= 1;
    if (step === 1) return contact.email && contact.phone;
    if (step === 2) return travelers.every((t) => t.full_name && t.passport_number);
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const { data: bk } = await api.post("/bookings", {
        tour_slug: tour.slug,
        travel_date: format(date, "yyyy-MM-dd"),
        adults,
        children,
        travelers,
        contact_email: contact.email,
        contact_phone: contact.phone,
        notes: contact.notes,
        promo_code: promo?.code || null,
      });
      // Success: no online payment — team follows up within 24 hours.
      setBookingRef((bk.booking_id || "").slice(0, 8).toUpperCase());
      setStep(4);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code: promoInput.trim(),
        subtotal_usd: subtotal,
      });
      if (data.valid) {
        setPromo({ code: data.code, percent_off: data.percent_off, description: data.description });
        toast.success(`${data.code} applied — ${data.percent_off}% off`);
      } else {
        setPromo(null);
        toast.error(data.error || "Invalid promo code");
      }
    } catch (e) {
      toast.error("Failed to validate code");
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput("");
  };

  return (
    <div className="space-y-8" data-testid="booking-widget">
      {/* PROGRESS */}
      <div>
        <div className="flex items-center gap-2 mb-2 overflow-x-auto no-scrollbar">
          {STEPS.map((label, idx) => (
            <div key={label} className="flex items-center gap-2 flex-shrink-0">
              <div
                data-testid={`step-${idx}`}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                  idx <= step ? "bg-saffron text-white" : "bg-cream-200 text-foreground/50"
                }`}
              >
                {idx + 1}
              </div>
              <span className={`text-xs uppercase tracking-wider hidden sm:inline ${idx === step ? "text-saffron font-semibold" : "text-foreground/55"}`}>
                {label}
              </span>
              {idx < STEPS.length - 1 && <div className="w-8 h-px bg-cream-200" />}
            </div>
          ))}
        </div>
      </div>

      {/* STEP CONTENT */}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white border border-cream-200 rounded-sm p-7 min-h-[400px]">
          {step === 0 && (
            <div className="space-y-6" data-testid="step-dates">
              <div>
                <h3 className="font-serif text-2xl mb-1">Pick your dates</h3>
                <p className="text-sm text-foreground/65 font-light">Departures available year-round — best season Oct-Mar.</p>
              </div>
              <div>
                <Label>Travel start date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date"
                      className="w-full h-11 px-3 bg-cream border border-input rounded-md text-sm text-left flex items-center justify-between"
                    >
                      {date ? format(date, "EEE, MMM d, yyyy") : <span className="text-muted-foreground">Choose travel start date</span>}
                      <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <CounterField label="Adults" value={adults} setValue={setAdults} min={1} max={tour.max_group_size} testid="counter-adults" />
                <CounterField label={`Children (40% off)`} value={children} setValue={setChildren} min={0} max={10} testid="counter-children" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="step-contact">
              <div>
                <h3 className="font-serif text-2xl mb-1">Your contact details</h3>
                <p className="text-sm text-foreground/65 font-light">Confirmation & itinerary will be sent here.</p>
              </div>
              <div>
                <Label htmlFor="b-email">Email *</Label>
                <Input
                  id="b-email"
                  type="email"
                  required
                  data-testid="booking-email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="bg-cream"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label htmlFor="b-phone">Phone (with country code) *</Label>
                <Input
                  id="b-phone"
                  required
                  data-testid="booking-phone"
                  value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  className="bg-cream"
                  placeholder="+44 7700 900123"
                />
              </div>
              <div>
                <Label htmlFor="b-notes">Special requests (optional)</Label>
                <Textarea
                  id="b-notes"
                  data-testid="booking-notes"
                  value={contact.notes}
                  onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                  rows={4}
                  className="bg-cream"
                  placeholder="Dietary requirements, accessibility needs, anniversary, etc."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-passport">
              <div>
                <h3 className="font-serif text-2xl mb-1">Traveler & passport details</h3>
                <p className="text-sm text-foreground/65 font-light">As they appear on your passport. Required for India visa & hotel check-ins.</p>
              </div>
              {travelers.map((tv, i) => (
                <div key={i} className="border border-cream-200 rounded-sm p-5 bg-cream-100 space-y-4" data-testid={`traveler-${i}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-saffron font-semibold">
                    <User className="w-3.5 h-3.5" /> Traveler {i + 1} · {tv.type === "child" ? "Child" : "Adult"}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Full name (as on passport) *</Label>
                      <Input value={tv.full_name} required onChange={(e) => updateTraveler(i, "full_name", e.target.value)} data-testid={`t-name-${i}`} className="bg-white" placeholder="Jane Marie Doe" />
                    </div>
                    <div>
                      <Label>Nationality</Label>
                      <Input value={tv.nationality} onChange={(e) => updateTraveler(i, "nationality", e.target.value)} data-testid={`t-nation-${i}`} className="bg-white" placeholder="British" />
                    </div>
                    <div>
                      <Label>Passport number *</Label>
                      <Input value={tv.passport_number} required onChange={(e) => updateTraveler(i, "passport_number", e.target.value)} data-testid={`t-passport-${i}`} className="bg-white" placeholder="GB123456789" />
                    </div>
                    <div>
                      <Label>Passport expiry</Label>
                      <Input type="date" value={tv.passport_expiry} onChange={(e) => updateTraveler(i, "passport_expiry", e.target.value)} data-testid={`t-expiry-${i}`} className="bg-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-review">
              <h3 className="font-serif text-2xl mb-1">Review & send request</h3>
              <p className="text-sm text-foreground/65 font-light">
                We&rsquo;ll receive your booking request instantly and a human from our Agra office will reach out
                within 24 hours to confirm availability and payment options.
              </p>
              <div className="bg-cream-100 rounded-sm border border-cream-200 p-5 space-y-2 text-sm">
                <Row label="Tour" value={tour.title} />
                <Row label="Travel date" value={date ? format(date, "MMM d, yyyy") : "—"} />
                <Row label="Travelers" value={`${adults} adults · ${children} children`} />
                <Row label="Contact" value={contact.email} />
                <Row label="Phone" value={contact.phone} />
              </div>

              {/* PROMO CODE */}
              <div className="bg-marigold/10 border border-marigold/30 rounded-sm p-4 space-y-3" data-testid="promo-block">
                <div className="flex items-center gap-2 text-sm font-medium text-deepred">
                  <Tag className="w-4 h-4 text-saffron" /> Have a promo code?
                </div>
                {!promo ? (
                  <div className="flex gap-2">
                    <Input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="DIWALI10"
                      className="bg-white uppercase tracking-wider"
                      data-testid="promo-input"
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyPromo())}
                    />
                    <button
                      type="button"
                      onClick={applyPromo}
                      disabled={promoChecking || !promoInput.trim()}
                      data-testid="promo-apply-btn"
                      className="px-5 bg-saffron hover:bg-deepred text-white text-sm uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50"
                    >
                      {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 bg-white border border-emerald-300 rounded-sm px-4 py-3" data-testid="promo-applied">
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div>
                        <div className="font-mono font-semibold text-saffron">{promo.code}</div>
                        <div className="text-xs text-foreground/70">
                          {promo.percent_off}% off · Save ${discount}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removePromo}
                      data-testid="promo-remove-btn"
                      className="text-foreground/40 hover:text-destructive"
                      aria-label="Remove promo"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-saffron-50 border border-saffron/30 p-4 rounded-sm text-xs flex gap-3 items-start">
                <Check className="w-5 h-5 text-saffron flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-foreground mb-1">No payment required now</div>
                  <div className="text-foreground/70">We reserve your dates first, then send a secure payment link once we confirm availability. Cancel anytime before payment.</div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6 text-center py-4" data-testid="step-confirmed">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-serif text-3xl mb-2">Booking request received</h3>
                <p className="text-sm text-foreground/65 font-light max-w-md mx-auto">
                  A confirmation just landed in your inbox at <b>{contact.email}</b>. Our team in Agra will reach out
                  within 24 hours to confirm details and share a secure payment link.
                </p>
              </div>
              {bookingRef && (
                <div className="inline-block bg-cream-100 border border-cream-200 rounded-sm px-4 py-2 text-sm">
                  <span className="text-foreground/55">Booking reference:</span>
                  <span className="ml-2 font-mono font-semibold text-deepred" data-testid="booking-ref">{bookingRef}</span>
                </div>
              )}
              <div className="pt-2">
                <a
                  href={`https://wa.me/917017798524?text=Hi%2C%20I%20just%20sent%20a%20booking%20request%20(ref%20${bookingRef}).%20Can%20we%20chat%3F`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3 rounded-sm text-sm uppercase tracking-wider transition-colors"
                  data-testid="whatsapp-followup"
                >
                  Chat with us on WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>

        {/* SUMMARY */}
        <aside className="bg-deepred text-white rounded-sm p-7 h-fit lg:sticky lg:top-48" data-testid="booking-summary">
          <div className="text-[10px] uppercase tracking-[0.3em] text-marigold mb-3">Booking Summary</div>
          <div className="font-serif text-2xl text-white leading-tight mb-5">{tour.title}</div>
          <div className="space-y-2 text-sm text-white/80 mb-6">
            <div className="flex justify-between"><span>Duration</span><span>{tour.duration}</span></div>
            <div className="flex justify-between"><span>Travel date</span><span>{date ? format(date, "MMM d") : "—"}</span></div>
            <div className="flex justify-between"><span>Adults × {fmtPrice(unit)}</span><span>{adults} × {fmtPrice(unit)} = {fmtPrice(unit * adults)}</span></div>
            <div className="flex justify-between"><span>Children × {fmtPrice(unit * 0.6)}</span><span>{children} × {fmtPrice(unit * 0.6)} = {fmtPrice(unit * 0.6 * children)}</span></div>
            {promo && (
              <div className="flex justify-between pt-2 border-t border-white/10 text-marigold">
                <span>Promo ({promo.code})</span>
                <span data-testid="summary-discount">−{fmtPrice(discount)}</span>
              </div>
            )}
          </div>
          <div className="border-t border-white/20 pt-5 mb-6">
            <div className="flex justify-between items-end">
              <span className="text-sm text-white/80">Total ({currency.code})</span>
              <span className="font-serif text-4xl text-marigold" data-testid="booking-total">{fmtPrice(total)}</span>
            </div>
          </div>

          <div className="space-y-2">
            {step > 0 && step < 4 && (
              <button onClick={prev} data-testid="booking-prev" className="w-full px-5 py-3 border border-white/40 text-white text-sm uppercase tracking-wider hover:bg-white hover:text-deepred transition-all rounded-sm">
                <ArrowLeft className="w-4 h-4 mr-2 inline" /> Back
              </button>
            )}
            {step < 3 && (
              <button onClick={next} disabled={!canProceed()} data-testid="booking-next" className="w-full bg-marigold text-deepred-700 px-5 py-3 font-semibold text-sm uppercase tracking-wider hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all rounded-sm">
                Continue <ArrowRight className="w-4 h-4 ml-2 inline" />
              </button>
            )}
            {step === 3 && (
              <button onClick={submitBooking} disabled={submitting} data-testid="booking-submit" className="w-full bg-marigold text-deepred-700 px-5 py-3 font-semibold text-sm uppercase tracking-wider hover:bg-white disabled:opacity-50 transition-all rounded-sm">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Sending…</> : <>Send booking request</>}
              </button>
            )}
          </div>
          {step < 4 && (
            <p className="text-xs text-white/55 text-center mt-4">No payment now — our team confirms within 24 hours.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-foreground/55">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function CounterField({ label, value, setValue, min, max, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <button
          type="button"
          onClick={() => setValue(Math.max(min, value - 1))}
          disabled={value <= min}
          data-testid={`${testid}-dec`}
          className="w-10 h-10 rounded-sm bg-cream-200 hover:bg-saffron hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>
        <div className="flex-1 text-center bg-cream border border-input rounded-sm h-10 flex items-center justify-center font-semibold" data-testid={testid}>{value}</div>
        <button
          type="button"
          onClick={() => setValue(Math.min(max, value + 1))}
          disabled={value >= max}
          data-testid={`${testid}-inc`}
          className="w-10 h-10 rounded-sm bg-cream-200 hover:bg-saffron hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
